import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import { currentRecentsKey } from '../../lib/api/recents-key.js';
import type { Conversation } from '../types';

const CHANNEL_NAME = 'research-assistant';

export type BroadcastEvent =
  | { type: 'read'; convId: string; at: number }
  | { type: 'unread'; convId: string; at: number };

type RecentsCache = { pages: Conversation[][]; pageParams: number[] };

let channel: BroadcastChannel | null = null;

function getChannel(): BroadcastChannel | null {
  if (typeof BroadcastChannel === 'undefined') return null;
  if (!channel) channel = new BroadcastChannel(CHANNEL_NAME);
  return channel;
}

// Inline cache patch — direct setQueryData, no TanStack useMutation
// wrapper. The previous attempt (commit 06484f7) routed reads through a
// useMutation whose `onSettled` invalidate raced with the SSE / cross-tab
// paints and reverted the dot back to read. Same cache shape, but one
// writer per call site.
function patchRecents(qc: QueryClient, id: string, unread: boolean): void {
  qc.setQueryData<RecentsCache>(currentRecentsKey(), (old) => {
    if (!old) return old;
    return {
      ...old,
      pages: old.pages.map((p) =>
        p.map((c) => (c.id === id ? { ...c, unread } : c)),
      ),
    };
  });
}

export function broadcastRead(convId: string): void {
  getChannel()?.postMessage({
    type: 'read',
    convId,
    at: Date.now(),
  } satisfies BroadcastEvent);
}

export function broadcastUnread(convId: string): void {
  getChannel()?.postMessage({
    type: 'unread',
    convId,
    at: Date.now(),
  } satisfies BroadcastEvent);
}

/**
 * Subscribe to mark-read / mark-unread events from other tabs and apply
 * them to the local TanStack cache so the sidebar dot stays in sync
 * without a server round-trip per tab.
 */
export function useBroadcastChannelSync(): void {
  const qc = useQueryClient();
  useEffect(() => {
    const ch = getChannel();
    if (!ch) return;
    const handler = (e: MessageEvent<BroadcastEvent>): void => {
      const msg = e.data;
      if (!msg || typeof msg !== 'object') return;
      if (msg.type === 'read') patchRecents(qc, msg.convId, false);
      else if (msg.type === 'unread') patchRecents(qc, msg.convId, true);
    };
    ch.addEventListener('message', handler);
    return () => ch.removeEventListener('message', handler);
  }, [qc]);
}
